export interface HelpEntry {
  title: string;
  summary: string;
  detail: string;
  learnMoreUrl?: string;
}

/**
 * Centralized help content keyed by feature area.
 * Used by HelpDrawer component.
 * learnMoreUrl points to in-app /help page sections.
 */
export const helpContent: Record<string, HelpEntry> = {
  dagCanvas: {
    title: 'Pipeline Canvas (DAG)',
    summary:
      'The Pipeline view shows your data workflow as connected nodes from source to destination.',
    detail:
      'Each node represents a step in your pipeline: source data, transforms, mapping, pipeline output, and destination. ' +
      'Click any node to inspect its data, code, or schema in the Inspector Panel below. ' +
      'Add new transforms using the + button or Command Palette (Cmd+K).',
    learnMoreUrl: '/help#building-pipelines',
  },

  mappingCanvas: {
    title: 'Mapping Editor',
    summary:
      'The Mapping Editor shows field-level connections between source and target schemas.',
    detail:
      'Source fields appear on the left, target fields on the right. Lines between them represent mappings. ' +
      'Each mapping shows a confidence score. You can click any connection to inspect or edit the mapping expression. ' +
      'Use the tabular view for a spreadsheet-like editing experience.',
    learnMoreUrl: '/help#mapping-fields',
  },

  commandPalette: {
    title: 'Command Palette',
    summary:
      'Press Cmd+K (or Ctrl+K) to quickly search and add transform nodes to your pipeline.',
    detail:
      'The Command Palette provides searchable access to all available transform types including SQL Transform, ' +
      'Python Transform, Fuzzy Match, LLM Extract, LLM Classify, and API Enrich. ' +
      'Type to filter, then click to add the transform to your pipeline.',
    learnMoreUrl: '/help#building-pipelines',
  },

  inspectorCode: {
    title: 'Code Tab',
    summary:
      'View and edit the SQL or Python code for the selected operation.',
    detail:
      'The Code tab shows the query or script that generates the selected node\'s output. ' +
      'You can modify the code directly, then use Preview to test changes before committing them.',
    learnMoreUrl: '/help#building-pipelines',
  },

  inspectorData: {
    title: 'Data Tab',
    summary:
      'Preview output rows and column statistics for the selected node.',
    detail:
      'The Data tab displays a sample of the output data in a table grid. ' +
      'Click any column header to open the Column Profile drawer with detailed statistics including ' +
      'null percentage, cardinality, top values, and data type. ' +
      'Use the toolbar to filter, sort, group, join, and apply other data operations.',
    learnMoreUrl: '/help#exploring-data',
  },

  inspectorSchema: {
    title: 'Schema Tab',
    summary:
      'Compare the current output schema against the target schema.',
    detail:
      'The Schema tab shows column-by-column comparison between what your pipeline produces and what the target expects. ' +
      'Mismatches are highlighted with guardrail warnings to help you catch issues before export.',
    learnMoreUrl: '/help#quality-review',
  },

  mappingModes: {
    title: 'Mapping Modes',
    summary:
      'Choose between Mapping Engine (deterministic) and Iterative AI-Assisted (conversational) mapping.',
    detail:
      'Mapping Engine is the recommended mode for production use. It generates deterministic, reproducible mappings ' +
      'based on schema analysis and data profiling. ' +
      'Iterative AI-Assisted mode provides a conversational experience where you can guide the AI through ' +
      'the mapping process step by step, useful for exploratory or complex mapping scenarios.',
    learnMoreUrl: '/help#mapping-fields',
  },

  bulkApprove: {
    title: 'Bulk Approve Mappings',
    summary:
      'Select rows in the Mapping Table View, then click Approve to approve selected mappings.',
    detail:
      'In the tabular Mapping View, use the checkboxes to select individual mappings or use the header checkbox ' +
      'to select all visible mappings. Then click the Approve button in the toolbar to approve them in bulk. ' +
      'You can also approve individual mappings via the three-dot action menu on each row.',
    learnMoreUrl: '/help#quality-review',
  },

  transformTypes: {
    title: 'Transform Types',
    summary:
      'SQL and Python transforms are available for data manipulation.',
    detail:
      'SQL Transform: Write SQL queries against workspace tables to filter, join, aggregate, or reshape data. ' +
      'Python Transform: Write Python code using pandas DataFrames for complex data manipulation. ' +
      'Both types can be chained together in the pipeline. Access them via Add Transform or the Command Palette (Cmd+K).',
    learnMoreUrl: '/help#building-pipelines',
  },

  columnProfile: {
    title: 'Column Profile',
    summary:
      'Click any column header in the Data tab to see detailed column statistics.',
    detail:
      'The Column Profile drawer shows: data quality bar, null percentage, cardinality (unique values count), ' +
      'top values with frequency histogram, and detected data type. ' +
      'This helps you quickly assess data quality and understand column distributions without leaving the workspace.',
    learnMoreUrl: '/help#exploring-data',
  },

  csvExport: {
    title: 'Export as CSV',
    summary:
      'Download query results as a CSV file from the Data Explorer.',
    detail:
      'After running a query in the Data tab, click the download icon in the results toolbar to export the ' +
      'current results as a CSV file. The file is named {tableName}_export.csv.',
    learnMoreUrl: '/help#exploring-data',
  },

  distinctToggle: {
    title: 'Distinct Toggle',
    summary: 'Toggle SELECT DISTINCT to remove duplicate rows from your query results.',
    detail:
      'Click the Distinct icon in the Data Operations toolbar to add SELECT DISTINCT to your query. ' +
      'The icon turns blue when active. Click again to remove it.',
    learnMoreUrl: '/help#building-pipelines',
  },

  reviewFilters: {
    title: 'Mapping Review Filters',
    summary:
      'Filter mappings by status to focus on items that need attention.',
    detail:
      'Use the filter tabs in the Mapping Review panel: All (shows everything), Needs Review (flagged for review), ' +
      'Unmapped (target fields with no source), and Low Confidence (below confidence threshold). ' +
      'Click any mapping to inspect it in the Inspector panel.',
    learnMoreUrl: '/help#quality-review',
  },

  schemaStates: {
    title: 'Schema States',
    summary:
      'Schemas progress through Draft, Active, Archived, and Deprecated states.',
    detail:
      'Draft: uploaded but not yet promoted. Active: promoted and ready for mapping. ' +
      'Archived: no longer actively used but preserved. Deprecated: marked as outdated. ' +
      'Schema states are shown as color-coded chips in the Formats page.',
    learnMoreUrl: '/help#schema-management',
  },

  batchLifecycle: {
    title: 'Batch Status',
    summary:
      'Track batch progress through the processing lifecycle.',
    detail:
      'Batches progress through: Pending → Profiling → Mapping → Mapped → Reviewing → Approved → ' +
      'Transforming → Loading → Completed (or Failed). Status is shown as a color-coded chip and ' +
      'auto-refreshes every 5 seconds on the Batch Detail page.',
    learnMoreUrl: '/help#projects-batches',
  },
};

/** All help content keys, typed for use in components */
export type HelpContentKey = keyof typeof helpContent;
